/**
 * Spanish locale for webhooks-ui.
 * Export is the "webhooks" subtree: host apps merge this into their i18n
 * resources as translation.webhooks so that t('webhooks.list.integrated') etc. resolve.
 */
import type { WebhooksLocale } from './en'

export const webhooksEs = {
  common: {
    createIntegration: `Crear integración`,
    updateIntegration: `Actualizar integración`,
    failedToSave: `Error al guardar`,
    learnMore: `más información`,
    beta: `Beta`,
  },
  list: {
    failedToLoad: `Error al cargar integraciones`,
    noIntegrationsAvailable: `No hay integraciones disponibles`,
    integrated: `Integrado`,
    learnMore: `Más información`,
  },
  detail: {
    status: `Estado`,
    on: `Activado`,
    off: `Desactivado`,
    failedToLoad: `Error al cargar la integración`,
    back: `← Volver`,
    webhooksV2Title: `Webhooks v2.0`,
    xlDentTitle: `Integración para Screen pops`,
    integrationTitle: `Integración para {{name}}`,
    webhooksV2Description: `El contenido del evento se enviará a la URL de devolución de llamada que proporcione. Debe especificar qué eventos enviar mediante los Selectores de eventos`,
    xlDentDescription: `Sepa quién llama. Ahorre tiempo. Obtenga las respuestas rápidamente. Reciba notificaciones emergentes en llamadas entrantes y salientes`,
    remoteIdLabel: `ID remoto`,
    practiceGuidLabel: `GUID de práctica`,
  },
  dolphin: {
    webhookUrl: `URL de Webhook`,
  },
  webhooksV2: {
    remoteId: `ID remoto`,
    remoteIdPlaceholder: `Se usa para identificar el PBX de origen.`,
    callbackUrl: `URL de devolución de llamada`,
    callbackUrlPlaceholder: `https://yourdomain.com/mango/event-handler`,
    eventSelectors: `Selectores de eventos`,
    eventSelectorsLoadError: `No se pudieron cargar los selectores de eventos — actualice la página para intentarlo de nuevo.`,
    noEventsWarning: `No ha elegido ningún selector de eventos, lo que resultará en que no se envíen eventos a esa URL. ¿Guardar de todos modos?`,
    goBack: `Volver`,
    saveAnyway: `Guardar de todos modos`,
  },
  xlDent: {
    tableDescription: `Asigne las extensiones de Mango Voice a la dirección IP de la estación de trabajo.`,
    extensionName: `Nombre de extensión`,
    extensionNumber: `N.º de extensión`,
    workstationIp: `Dirección IP de la estación de trabajo`,
    enableDisable: `Activar/Desactivar`,
    disabled: `Desactivado.`,
    ipPlaceholder: `e.g. 192.168.1.1:8888`,
    update: `Actualizar`,
    devicesNote: `Nota: Cualquier cambio requerirá actualizar el dispositivo en 'Mis dispositivos' para que los cambios surtan efecto.`,
  },
  // El texto de esta sección está alineado intencionalmente con apidocs.mangovoice.com
  // para garantizar coherencia en el lenguaje entre el portal y la documentación de la API.
  authKeys: {
    title: `Claves de autorización`,
    description: `Genera un token de tres partes que autentica las solicitudes a las APIs globales de Mango Voice. Compártelo con tu socio de integración para que pueda hacer llamadas a la API en tu nombre.`,
    confidential: `Los tokens deben mantenerse confidenciales. Exponer los tokens al público puede resultar en robo, pérdida o corrupción de datos.`,
    noKeys: `Aún no se han generado claves de autorización.`,
    generate: `Generar`,
    regenerate: `Regenerar`,
    remove: `Eliminar`,
    copy: `Copiar`,
    copied: `¡Copiado!`,
    apiDocsLabel: `Ver documentación de la API`,
    failedToLoad: `Error al cargar las claves de autorización`,
    failedToGenerate: `Error al generar las claves de autorización`,
    failedToRegenerate: `Error al regenerar las claves de autorización`,
    failedToRemove: `Error al eliminar las claves de autorización`,
    confirmRegenerateTitle: `Regenerar claves de autorización`,
    confirmRegenerateBody: `Regenerar las claves de autorización puede deshabilitar las integraciones existentes. ¿Estás seguro de que deseas continuar?`,
    confirmRemoveTitle: `Eliminar claves de autorización`,
    confirmRemoveBody: `Eliminar las claves de autorización puede deshabilitar las integraciones existentes. ¿Estás seguro de que deseas continuar?`,
    confirmCancel: `Cancelar`,
    confirmProceed: `Proceder`,
    combinedToken: `Token combinado`,
    combinedTokenHint: `Utilizado como variables de ruta en las solicitudes de la API`,
    formatIdent1: `ident1`,
    formatIdent2: `ident2`,
    formatIdent3: `ident3`,
  },
  admin: {
    apiAccess: {
      title: `Acceso a la API`,
      selectPbx: `Seleccionar PBX`,
      selectCompanyFirst: `Primero seleccione una empresa`,
      noPbxSelected: `Seleccione una empresa y un PBX arriba para administrar las claves de autorización.`,
    },
    // admin.list is an internal admin3 namespace — English strings are intentional
    list: {
      title: `Webhook Integrations`,
      description: `View and manage webhook integration status across all PBXes for a company`,
      companyLabel: `Company`,
      companyPlaceholder: `Select a company…`,
      pbxFilter: `PBX`,
      allPbxes: `All PBXes`,
      integrationFilter: `Integration`,
      allIntegrations: `All Integrations`,
      noCompanySelected: `Select a company to view integrations`,
      noIntegrationsFound: `No integrations found`,
      columnStatus: `Status`,
      columnActive: `Active`,
      notIntegrated: `Not integrated`,
    },
  },
} satisfies WebhooksLocale
